import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { IColorMap } from '../interfaces/explorer/interfaces/colormap/IColorMap';
import { IColorMapResponse } from '../interfaces/explorer/interfaces/colormap/IColorMapResponse';
import { TColormap } from '../types/explorer/sjmap2/colormap/TColormap';
import { IValuedColormapEntry } from '../interfaces/sjmap2/raster/colormap/colormap-entries/IValuedColormapEntry';
import { IRangedColormapEntry } from '../interfaces/sjmap2/raster/colormap/colormap-entries/IRangedColormapEntry';
import { INamedColormap } from '../interfaces/sjmap2/raster/colormap/INamedColormap';
import { IDiscreteColormap } from '../interfaces/sjmap2/raster/colormap/IDiscreteColormap';
import { IContinuousColormap } from '../interfaces/sjmap2/raster/colormap/IContinuousColormap';
import { IRangeColormap } from '../interfaces/sjmap2/raster/colormap/IRangeColormap';
import { environment } from '../../environments/environment';
import { ColorUtils } from '../utility/color.utils';

type RgbaEntry = [number, number, number, number];
type ClipInterval = [[number, number], RgbaEntry];

export interface ITitilerTileJSON {
  tilejson: string;
  tiles: string[];
  minzoom?: number;
  maxzoom?: number;
  bounds?: [number, number, number, number];
  center?: [number, number, number];
}

@Injectable({
  providedIn: 'root',
})
export class TitilerService {
  private readonly MAP_SERVER_URL = environment.services.urlTITILER;
  private readonly INFO_ENDPOINT = `${this.MAP_SERVER_URL}/cog/info.geojson`;
  private readonly STATS_ENDPOINT = `${this.MAP_SERVER_URL}/cog/statistics`;
  private readonly WMTS_ENDPOINT = `${this.MAP_SERVER_URL}/cog/WebMercatorQuad/WMTSCapabilities.xml`;
  private readonly TILER_URL_TEMPLATE = `${this.MAP_SERVER_URL}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}@1x`;
  private readonly TILEJSON_ENDPOINT = `${this.MAP_SERVER_URL}/cog/WebMercatorQuad/tilejson.json`;

  private readonly namedColormapCache = new Map<string, RgbaEntry[]>();
  private readonly tileJsonCache = new Map<string, Observable<ITitilerTileJSON>>();

  constructor(private http: HttpClient) {}

  // getTilesURL(cogUrl: string, colormap: string): string {
  //   const _colormap : TColormap = JSON.parse(colormap) as TColormap; // DA CANCELLARE
  //   const encodedUrl = encodeURIComponent(cogUrl);
  //   return `${this.TILER_URL_TEMPLATE}?url=${encodedUrl}&${this.fromColormapToTitiler(_colormap)}`
  // }
  getTilesURL(
    cogUrl: string,
    colormap: TColormap,
    options?: { min?: number; max?: number; bidx?: number },
  ): string {
    return `${this.TILER_URL_TEMPLATE}?${this.buildCogRenderParams(
      cogUrl,
      colormap,
      options,
    ).join('&')}`;
  }

  getTileJSON(
    cogUrl: string,
    colormap: TColormap,
    options?: { min?: number; max?: number; bidx?: number },
  ): Observable<ITitilerTileJSON> {
    const requestUrl = `${this.TILEJSON_ENDPOINT}?${this.buildCogRenderParams(
      cogUrl,
      colormap,
      options,
    ).join('&')}`;
    const cachedRequest = this.tileJsonCache.get(requestUrl);
    if (cachedRequest) {
      return cachedRequest;
    }

    const request = this.http.get<ITitilerTileJSON>(requestUrl).pipe(
      catchError((error) => {
        this.tileJsonCache.delete(requestUrl);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.tileJsonCache.set(requestUrl, request);
    return request;
  }

  private buildCogRenderParams(
    cogUrl: string,
    colormap: TColormap,
    options?: { min?: number; max?: number; bidx?: number },
  ): string[] {
    const params: string[] = [`url=${encodeURIComponent(cogUrl)}`];
    if (options?.bidx !== undefined) {
      params.push(`bidx=${options.bidx}`);
    }

    if (colormap.type) {
      params.push(this.fromColormapToTitiler(colormap));
    } else {
      const minVal = options?.min ?? 0;
      const maxVal = options?.max ?? 255;
      params.push(`rescale=${minVal},${maxVal}`);
    }

    return params;
  }

  // per raster a 3 bande
  // getTilesURL(cogUrl: string, colormap: TColormap): string {
  //   const encodedUrl = encodeURIComponent(cogUrl);
  //   return `${this.TILER_URL_TEMPLATE}?url=${encodedUrl}&bidx=1&bidx=2&bidx=3&rescale=0,10000&nodata=0&transparent=true`;;
  // }

  fromColormapToTitiler(colormap: TColormap): string {
    if (colormap.type === 'named') {
      return this.fromNamedColormapToTitiler(colormap);
    }
    if (colormap.type === 'discrete') {
      return this.fromDiscreteColormapToTitiler(colormap);
    }
    if (colormap.type === 'continuous') {
      return this.fromContinuousColormapToTitiler(colormap);
    }
    if (colormap.type === 'range') {
      return this.fromRangeColormapToTitiler(colormap);
    }

    throw new Error(`Unsupported colormap type, colormap: ${colormap}`);
  }

  private fromNamedColormapToTitiler(namedColormap: INamedColormap): string {
    return `rescale=${namedColormap.min},${namedColormap.max}&colormap_name=${namedColormap.colormapName}`;
  }

  private fromValuedColormapToTitilerJSON(
    valuedEntries: IValuedColormapEntry[],
  ): { [key: string]: string } {
    return Object.fromEntries(
      valuedEntries.map((valuedEntry: IValuedColormapEntry) => [
        valuedEntry.value,
        valuedEntry.color,
      ]),
    );
  }

  private fromDiscreteColormapToTitiler(
    discreteColormap: IDiscreteColormap,
  ): string {
    const titilerObj: { [key: string]: string } =
      this.fromValuedColormapToTitilerJSON(discreteColormap.colormap);
    const URIColormap = encodeURIComponent(JSON.stringify(titilerObj));
    return `colormap=${URIColormap}`;
  }

  private fromContinuousColormapToTitiler(
    continuousColormap: IContinuousColormap,
  ): string {
    const entries = [...continuousColormap.colormap].sort(
      (a, b) => a.value - b.value,
    );

    // We expect at least two entries for a continuous map
    if (entries.length < 2) {
      if (entries.length === 1) {
        const titilerObj = {
          '0': entries[0].color,
          '255': entries[0].color,
        };
        const URIColormap = encodeURIComponent(JSON.stringify(titilerObj));
        // If only one value, we can't really rescale effectively, but let's try
        // to default min/max or use the single value
        return `rescale=${entries[0].value},${entries[0].value}&colormap=${URIColormap}`;
      }
      return '';
    }

    const minVal = entries[0].value;
    const maxVal = entries[entries.length - 1].value;
    const range = maxVal - minVal;

    const titilerObj: { [key: string]: string } = {};

    // Generate 256 steps (0..255)
    for (let i = 0; i < 256; i++) {
      // Map current index i (0..255) to a real value in the data range
      const currentVal = minVal + (i / 255) * range;

      // Find which two entries this currentVal falls between
      // We look for the first entry that is greater than currentVal
      let upperIndex = entries.findIndex((e) => e.value >= currentVal);

      // Edge cases
      if (upperIndex === -1) {
        // currentVal is above the highest defined value (shouldn't happen with the loop logic, but safe fallback)
        titilerObj[i.toString()] = entries[entries.length - 1].color;
        continue;
      }
      if (upperIndex === 0) {
        // currentVal is below or equal to the first value
        titilerObj[i.toString()] = entries[0].color;
        continue;
      }

      const lowerEntry = entries[upperIndex - 1];
      const upperEntry = entries[upperIndex];

      // Linear interpolation factor (0..1) within this segment
      // Avoid division by zero if two entries have same value
      const segmentRange = upperEntry.value - lowerEntry.value;
      let t = 0;
      if (segmentRange > 0) {
        t = (currentVal - lowerEntry.value) / segmentRange;
      }

      titilerObj[i.toString()] = ColorUtils.interpolateColors(
        lowerEntry.color,
        upperEntry.color,
        t,
      );
    }

    const URIColormap = encodeURIComponent(JSON.stringify(titilerObj));
    return `rescale=${minVal},${maxVal}&colormap=${URIColormap}`;
  }

  private fromRangeColormapToTitiler(rangeColormap: IRangeColormap): string {
    const titilerObj: [[number, number], string][] = rangeColormap.colormap.map(
      (entry: IRangedColormapEntry) => [
        [entry.startValue, entry.endValue],
        entry.color,
      ],
    );
    const URIColormap = encodeURIComponent(JSON.stringify(titilerObj));
    return `colormap=${URIColormap}`;
  }

  // getPointRasterValue
  // Retrieve point data from raster on click event
  getPointRasterValue(
    cogUrl: string,
    lon: number,
    lat: number,
  ): Observable<unknown> {
    const pointDataUrl = `${this.MAP_SERVER_URL}/cog/point/${lon},${lat}?url=${encodeURIComponent(cogUrl)}`;
    return this.http.get(pointDataUrl);
  }

  // Fetch metadata for raster data
  getRasterInfo(): Observable<unknown> {
    return this.http.get(this.INFO_ENDPOINT);
  }

  // Fetch raster statistics
  getRasterStatistics(cogUrl: string, body: unknown): Observable<unknown> {
    const statsUrl = `${this.STATS_ENDPOINT}?coord_crs=epsg:3857&url=${encodeURIComponent(cogUrl)}`;
    return this.http.post(statsUrl, body);
  }

  // TODO DA REFATTORIZZARE PERCHé SOLO PER S3
  getWMTSLink(
    cogUrl: string,
    min: number,
    max: number,
    colormap: string,
  ): string {
    const encodedUrl = encodeURIComponent(cogUrl); // Only encode once
    return `${this.WMTS_ENDPOINT}?url=${encodedUrl}&rescale=${min},${max}&colormap_name=${colormap}`;
  }

  // ── Clip (transparency outside range) ──────────────────────────────────

  /**
   * Fetches the 256 RGBA entries for a named colormap from titiler and caches
   * the result so subsequent calls are instant.
   */
  getNamedColormapEntries(name: string): Observable<RgbaEntry[]> {
    if (this.namedColormapCache.has(name)) {
      return of(this.namedColormapCache.get(name)!);
    }
    return this.http
      .get<Record<string, number[]> | { colormap: Record<string, number[]> }>(
        `${this.MAP_SERVER_URL}/colorMaps/${name}`,
      )
      .pipe(
        map((res) => {
          const raw: Record<string, number[]> =
            (res as { colormap?: Record<string, number[]> }).colormap ?? (res as Record<string, number[]>);
          const entries: RgbaEntry[] = Array.from({ length: 256 }, (_, i) => {
            const e = raw[i] ?? raw[i.toString()];
            return e
              ? ([e[0], e[1], e[2], e[3] ?? 255] as RgbaEntry)
              : ([0, 0, 0, 0] as RgbaEntry);
          });
          this.namedColormapCache.set(name, entries);
          return entries;
        }),
      );
  }

  /**
   * Builds a tile URL that only renders pixel values in [clipMin, clipMax].
   * Everything outside that range becomes transparent via titiler's
   * interval-based colormap format (no explicit alpha=0 needed — unmatched
   * intervals are transparent by default).
   */
  getTilesURLWithClip(
    cogUrl: string,
    colormap: TColormap,
    clipMin: number,
    clipMax: number,
    namedEntries?: RgbaEntry[],
    options?: { bidx?: number },
  ): string {
    const encodedUrl = encodeURIComponent(cogUrl);
    const params: string[] = [`url=${encodedUrl}`];
    if (options?.bidx !== undefined) {
      params.push(`bidx=${options.bidx}`);
    }
    const clipParam = this.buildClipParam(colormap, clipMin, clipMax, namedEntries);
    if (clipParam) {
      params.push(clipParam);
    }
    return `${this.TILER_URL_TEMPLATE}?${params.join('&')}`;
  }

  private buildClipParam(
    colormap: TColormap,
    clipMin: number,
    clipMax: number,
    namedEntries?: RgbaEntry[],
  ): string {
    const intervals = this.buildClipIntervals(colormap, clipMin, clipMax, namedEntries);
    if (!intervals.length) return '';
    return `colormap=${encodeURIComponent(JSON.stringify(intervals))}`;
  }

  private buildClipIntervals(
    colormap: TColormap,
    clipMin: number,
    clipMax: number,
    namedEntries?: RgbaEntry[],
  ): ClipInterval[] {
    const N = 64;
    if (colormap.type === 'named' && namedEntries?.length) {
      return this.buildNamedClipIntervals(
        colormap as INamedColormap,
        clipMin,
        clipMax,
        namedEntries,
        N,
      );
    }
    if (colormap.type === 'continuous') {
      return this.buildContinuousClipIntervals(
        colormap as IContinuousColormap,
        clipMin,
        clipMax,
        N,
      );
    }
    return [];
  }

  private buildNamedClipIntervals(
    colormap: INamedColormap,
    clipMin: number,
    clipMax: number,
    entries: RgbaEntry[],
    steps: number,
  ): ClipInterval[] {
    const cmRange = (colormap.max - colormap.min) || 1;
    const stepSize = (clipMax - clipMin) / steps;
    const result: ClipInterval[] = [];

    for (let i = 0; i < steps; i++) {
      const start = clipMin + i * stepSize;
      const end =
        i < steps - 1
          ? clipMin + (i + 1) * stepSize
          : this.inclusiveIntervalEnd(clipMax);
      const mid = (start + end) / 2;
      const idx = Math.min(
        255,
        Math.max(0, Math.round(((mid - colormap.min) / cmRange) * 255)),
      );
      result.push([[start, end], entries[idx]]);
    }
    return result;
  }

  private buildContinuousClipIntervals(
    colormap: IContinuousColormap,
    clipMin: number,
    clipMax: number,
    steps: number,
  ): ClipInterval[] {
    const sorted = [...colormap.colormap].sort((a, b) => a.value - b.value);
    if (sorted.length < 2) return [];

    const stepSize = (clipMax - clipMin) / steps;
    const result: ClipInterval[] = [];

    for (let i = 0; i < steps; i++) {
      const start = clipMin + i * stepSize;
      const end =
        i < steps - 1
          ? clipMin + (i + 1) * stepSize
          : this.inclusiveIntervalEnd(clipMax);
      const mid = (start + end) / 2;
      result.push([[start, end], this.sampleContinuousColormap(sorted, mid)]);
    }
    return result;
  }

  /**
   * rio-tiler interval colormaps use half-open ranges: [start, end).
   * Move only the final bound to the next representable neighbourhood so a
   * value exactly equal to the slider maximum is included as users expect.
   */
  private inclusiveIntervalEnd(value: number): number {
    const epsilon = Math.max(1, Math.abs(value)) * Number.EPSILON * 16;
    return value + epsilon;
  }

  private sampleContinuousColormap(
    sorted: IValuedColormapEntry[],
    value: number,
  ): RgbaEntry {
    let upperIdx = sorted.findIndex((e) => e.value >= value);
    if (upperIdx === -1) {
      const rgb = ColorUtils.hexToRgb(sorted[sorted.length - 1].color);
      return [rgb.r, rgb.g, rgb.b, 255];
    }
    if (upperIdx === 0) {
      const rgb = ColorUtils.hexToRgb(sorted[0].color);
      return [rgb.r, rgb.g, rgb.b, 255];
    }
    const lower = sorted[upperIdx - 1];
    const upper = sorted[upperIdx];
    const segRange = upper.value - lower.value;
    const t = segRange > 0 ? (value - lower.value) / segRange : 0;
    const hex = ColorUtils.interpolateColors(lower.color, upper.color, t);
    const rgb = ColorUtils.hexToRgb(hex);
    return [rgb.r, rgb.g, rgb.b, 255];
  }

  // ── End clip ────────────────────────────────────────────────────────────

  getAvailableColorMaps(): Observable<IColorMap[]> {
    const url = `${this.MAP_SERVER_URL}/colorMaps`;

    return this.http.get<IColorMapResponse>(url).pipe(
      map((res) => {
        const colorMapNames: string[] = res.colorMaps.map((name) =>
          name.toLowerCase(),
        );

        const templateLink: string = res.links.find(
          (l) => l.rel === 'data' && l.type === 'image/png' && l.templated,
        )?.href;

        return colorMapNames.map((name) => ({
          id: name,
          href: templateLink
            ?.replace('{colorMapId}', name)
            .replace('format=png', 'format=webp'),
        }));
      }),
    );
  }
}
